import { Group, Object3DEventMap } from "three";

import { Direction, PieceId, Square, SquareFacts } from "src/api";
import { Tween } from "src/client/human/render/animation/Tween";
import { FactionModelInstance } from "src/client/human/render/models/GameModel";
import { MOVE_DURATION_SECONDS_PER_UNIT } from "src/client/human/render/renderConstants";
import { OutlineSettings } from "src/client/human/render/world/OutlineSettings";
import { World } from "src/client/human/render/world/World";
import {
  WorldObject,
  WorldObjectAPI,
  WorldObjectDefaultEvents
} from "src/client/human/render/world/WorldObject";
import { kCenteralLoader } from "src/client/human/singletons/CentralLoader";
import { Scheduler } from "src/shared/Scheduler";
import { createTypedEventEmitter } from "src/shared/util";

import { generateMoveSequenceForBoardUpdate } from "../../apis/MoveSequence";
import { BoardUpdate } from "../../updates/BoardUpdate";
import { BoardWorldObject } from "../../updates/BoardWorldObject";
import { BoardPiecesInstanceAPI } from "./BoardPiecesInstanceAPI";

export class BoardPieces
  extends WorldObject
  implements BoardWorldObject, BoardPiecesInstanceAPI
{
  public readonly root: Group<Object3DEventMap> = new Group();
  public readonly events = createTypedEventEmitter<WorldObjectDefaultEvents>();

  private world: World;
  private idToInstance = new Map<PieceId, FactionModelInstance>();
  private squareToId = new Map<Square, PieceId>();
  private previousSquareFacts: ReadonlyMap<Square, SquareFacts> = new Map<
    Square,
    SquareFacts
  >();
  private scheduler = new Scheduler();

  constructor(world: World) {
    super();
    this.world = world;
  }

  onBoardUpdate(boardUpdate: BoardUpdate): void {
    const sequence = generateMoveSequenceForBoardUpdate(
      this.previousSquareFacts,
      boardUpdate
    );
    this.previousSquareFacts = boardUpdate.facts.squares;

    const seenIds = new Set<PieceId>();
    const movedIds = new Set<PieceId>();
    const seenSquares = new Set<Square>();

    // Update our store for what's where.
    if (boardUpdate.delta.kind === "move") {
      for (const leg of boardUpdate.delta.legs) {
        if (leg.id === undefined) {
          this.squareToId.delete(leg.to);
          continue;
        }
        this.squareToId.set(leg.to, leg.id);
        const instance = this.idToInstance.get(leg.id);
        if (instance) instance.setSquareButDontMove(leg.to);
        seenIds.add(leg.id);
        seenSquares.add(leg.to);
      }
    }

    // If we were able to generate a move sequence, follow it.
    // This is still in development...
    if (sequence) {
      for (const move of sequence.moves) {
        const instance = this.idToInstance.get(move.pieceId);
        if (instance === undefined) continue;
        seenIds.add(move.pieceId);
        movedIds.add(move.pieceId);
        let moveLoops = 1;
        if (move.action === "moving") {
          moveLoops = move.timeDurationSeconds / MOVE_DURATION_SECONDS_PER_UNIT;
        }
        this.scheduler.add({
          startIn: move.timeOffsetSeconds,
          duration: move.timeDurationSeconds,
          invokeFunctionAtStart: () => {
            instance.playClipForAction(
              move.action,
              move.timeDurationSeconds / moveLoops,
              moveLoops
            );
            instance.setMoving({
              from: move.fromPosition ?? instance.root.position.clone(),
              to:
                move.toPosition ??
                move.fromPosition ??
                instance.root.position.clone(),
              fromQuaternion:
                move.fromQuaternion ?? instance.root.quaternion.clone(),
              toQuaternion:
                move.toQuaternion ?? instance.root.quaternion.clone(),
              tween: new Tween(move.timeDurationSeconds),
              tweenQuaternion: new Tween(move.timeDurationSeconds)
            });
          },
          invokeFunctionAtComplete: () => {
            if (move.action !== "dying") return;
            this.world.removeAndDispose(instance);
          }
        });
      }
    }

    for (const squareFacts of boardUpdate.facts.squares.values()) {
      const { piece, square } = squareFacts;
      if (!piece) continue;
      const wasMoved = movedIds.has(piece.id);
      seenIds.add(piece.id);
      seenSquares.add(square);
      let instance = this.idToInstance.get(piece.id);
      if (instance === undefined) {
        instance = kCenteralLoader.getPrimaryFactionModels().create(piece);
        this.placeInstance(instance, square, piece.facing);
        this.world.add(instance);
        this.idToInstance.set(instance.id, instance);
      } else if (instance.square !== square && !wasMoved) {
        this.placeInstance(instance, square, piece.facing);
      }
      this.squareToId.set(square, instance.id);
      instance.setPieceFacts(piece);
    }

    for (const [id, instance] of this.idToInstance) {
      if (seenIds.has(id)) continue;
      this.idToInstance.delete(id);
      // Special cut-out for things that are scheduled for removal.
      if (!movedIds.has(id)) this.world.removeAndDispose(instance);
    }

    for (const [square] of this.squareToId) {
      if (seenSquares.has(square)) continue;
      this.squareToId.delete(square);
    }
  }

  getPieceInstanceAt(square: Square): WorldObjectAPI | null {
    const instanceId = this.squareToId.get(square);
    if (instanceId === undefined) return null;
    return this.idToInstance.get(instanceId) ?? null;
  }

  private instanceOnSquare(square: Square): FactionModelInstance | undefined {
    for (const instance of this.idToInstance.values()) {
      if (instance.square === square) return instance;
    }
    return undefined;
  }

  private placeInstance(
    instance: FactionModelInstance,
    square: Square,
    facing: Direction
  ) {
    instance.moveToSquare(square);
    let direction = 0;
    switch (facing) {
      case "N":
        direction = Math.PI;
        break;
      case "E":
        direction = Math.PI / 2;
        break;
      case "S":
        direction = 0;
        break;
      case "W":
        direction = (Math.PI * 3) / 2;
        break;
      default:
        break;
    }
    instance.setDirection(direction);
  }

  isBoardUpdateFinished(): boolean {
    return true;
  }
  setOutline(
    _enabled: boolean,
    _settings?: Partial<OutlineSettings> | null
  ): void {
    // throw new Error("Method not implemented.");
  }
  getOutline(): OutlineSettings | null {
    return null;
    // throw new Error("Method not implemented.");
  }

  step(deltaSeconds: number): void {
    this.scheduler.step(deltaSeconds);
  }
  dispose(): void {
    for (const piece of this.idToInstance.values()) {
      piece.dispose();
    }
    this.idToInstance.clear();
  }
}
